import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowRight, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTAKE_QUESTIONS = [
  {
    key: "business_idea",
    label: "What digital product or offer are you building?",
    placeholder: "e.g., An online course teaching watercolor basics, a coaching program for new managers, a template bundle for wedding planners...",
    type: "textarea"
  },
  {
    key: "target_audience",
    label: "Who do you want to help?",
    placeholder: "e.g., Busy moms wanting a creative outlet, first-time managers in tech, engaged couples planning DIY weddings...",
    type: "textarea"
  },
  {
    key: "biggest_challenge",
    label: "What's been holding you back from launching?",
    placeholder: "e.g., Not sure where to start, overwhelmed by all the options, struggling to find time, unsure if anyone would buy...",
    type: "textarea"
  },
  {
    key: "desired_outcome",
    label: "What would success look like in 14 days?",
    placeholder: "e.g., A clear offer I feel confident about, a sales page ready to share, my first paying customer...",
    type: "textarea"
  }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [answers, setAnswers] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (currentStep < INTAKE_QUESTIONS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await fetch(`${API_URL}/api/user/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          answers,
          completed_at: new Date().toISOString()
        })
      });
      navigate('/journey', { replace: true });
    } catch (error) {
      console.error('Onboarding save failed:', error);
      navigate('/journey', { replace: true });
    }
  };

  const currentQuestion = INTAKE_QUESTIONS[currentStep];
  const isLastStep = currentStep === INTAKE_QUESTIONS.length - 1;
  const canProceed = answers[currentQuestion.key]?.trim().length > 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
      <div className="max-w-2xl w-full">
        {/* Progress Indicator */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-[#6B8E7C] uppercase tracking-wide font-medium">
              Your Blueprint Begins
            </span>
            <span className="text-xs text-[#2C2C2C]/40">
              {currentStep + 1} of {INTAKE_QUESTIONS.length}
            </span>
          </div>
          <div className="h-1 bg-[#E6E1D3] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#6B8E7C] rounded-full transition-all duration-500"
              style={{ width: `${((currentStep + 1) / INTAKE_QUESTIONS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Welcome Header (first step only) */}
        {currentStep === 0 && (
          <div className="mb-8 animate-fade-up opacity-0" style={{ animationFillMode: 'forwards' }}>
            <p className="text-[#6B8E7C] text-sm font-medium mb-2">
              Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
            </p>
            <h1 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Let's map your 14-day journey.
            </h1>
            <p className="text-[#2C2C2C]/60 font-light">
              A few questions to personalize your blueprint. Take your time.
            </p>
          </div>
        )}

        {/* Question Card */}
        <div 
          className="card-calm animate-fade-up opacity-0" 
          style={{ animationFillMode: 'forwards', animationDelay: '100ms' }}
          key={currentStep}
        >
          <label className="block text-xl mb-6 text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            {currentQuestion.label}
          </label>
          
          <Textarea
            value={answers[currentQuestion.key] || ''}
            onChange={(e) => handleChange(currentQuestion.key, e.target.value)}
            placeholder={currentQuestion.placeholder}
            className="input-calm min-h-[150px] resize-none border border-[#E5E0D6] rounded-xl p-4 text-lg"
            data-testid={`intake-${currentQuestion.key}`}
          />

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E5E0D6]">
            {currentStep > 0 ? (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="btn-ghost"
                data-testid="intake-back-btn"
              >
                Back
              </Button>
            ) : (
              <div />
            )}
            
            {isLastStep ? (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed || isSubmitting}
                className="btn-primary"
                data-testid="intake-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Preparing your blueprint...
                  </>
                ) : (
                  <>
                    Begin Your Journey
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed}
                className="btn-primary"
                data-testid="intake-next-btn"
              >
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Skip Option */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/journey', { replace: true })}
            className="text-sm text-[#2C2C2C]/40 hover:text-[#2C2C2C]/60 transition-colors"
            data-testid="skip-onboarding"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
